import { useAutoAnimate } from "@formkit/auto-animate/react";
import { useQueries, useQuery } from "@tanstack/react-query";
import React, { useEffect, useMemo, useState } from "react";
import { Badge, Button, Col, Form, ListGroup, ListGroupItem, Modal, Row, Spinner, Table } from "react-bootstrap";
import { ArrowDown, ArrowDownUp, ArrowUp, DashSquare, PlusSquare } from "react-bootstrap-icons";
import { useTranslation } from "react-i18next";
import { useAbsence } from "../../api/absence";
import { avAttendantApi, useAVAssignments } from "../../api/avAttendant";
import { congregationApi, useCongSettings } from "../../api/cong";
import { useFSMeetingLocations } from "../../api/fsmeetings";
import { midweekApi } from "../../api/midweek";
import { useOtherSchedule } from "../../api/otherSchedule";
import QueryKeys from "../../api/queryKeys";
import { useWMSchedules, weekendApi } from "../../api/weekend";
import { getOverlappingAssignmentLabel } from "../../helpers/conflictDetection";
import {
  dateIsBetween,
  daysInRange,
  dateStringCompare,
  dateToString,
  getDayjs,
  ISODateFormat,
  mmDate,
  stringToDate,
  stringToLocaleDate,
  weekOfString,
  wmDate,
} from "../../helpers/dateHelpers";
import HourglassGlobals from "../../helpers/globals";
import { userCompare } from "../../helpers/user";
import { HourMs } from "../../helpers/util";
import { QueryKeyBuilder } from "../../query/queryKeyBuilder";
import { DateRange, ISODateString } from "../../types/date";
import { Absence } from "../../types/scheduling/absence";
import { AVAttendantAsset, AVAttendantAssignment, AVAttendantAssignmentType } from "../../types/scheduling/avAttendant";
import { NotificationType } from "../../types/scheduling/meetings";
import {
  MidweekMeetingPart,
  MidweekMeetingPartAsset,
  MidweekMeetingPartType,
  MidweekScheduleInfo,
  MMs,
} from "../../types/scheduling/midweek";
import { FSMeetingLocation } from "../../types/scheduling/fsmeetings";
import { OtherSchedule, OtherScheduleKind } from "../../types/scheduling/other";
import { CongSettings } from "../../types/scheduling/settings";
import { WMCoreParts, WMCorePartTitleAsset, WMSchedule } from "../../types/scheduling/weekend";
import User, { UserGender } from "../../types/user";
import { avaLabel } from "./av_attendant/week";
import { OverlappingAssignment, StudentPartTypes } from "./common";

export type AssignmentSummaryType =
  | MidweekMeetingPartType
  | AVAttendantAssignmentType
  | WMCoreParts
  | OtherScheduleKind.FieldMinistry
  | "publicTalk"
  | "publicTalkOut";

export type AssignmentSummary = {
  date: ISODateString;
  type: AssignmentSummaryType;
  userId: number;
  classroom?: number;
  assistantId?: number;
  label?: string;
  slot?: number; // For AV assignments
  assignmentDate?: ISODateString;
};

type SortField = "name" | "all" | "type" | "student" | "prayer";

function mmScheduleToAssignments(
  si: MidweekScheduleInfo,
  partsById: Map<number, MidweekMeetingPart>,
): AssignmentSummary[] {
  const assignments: AssignmentSummary[] = [];
  const meetingDate = si.date;
  if (si.chairman) assignments.push({ date: meetingDate, type: MidweekMeetingPartType.Chairman, userId: si.chairman });
  if (si.chairman2)
    assignments.push({ date: meetingDate, type: MidweekMeetingPartType.Chairman2, userId: si.chairman2 });
  if (si.chairman3)
    assignments.push({ date: meetingDate, type: MidweekMeetingPartType.Chairman3, userId: si.chairman3 });
  if (si.openprayer)
    assignments.push({ date: meetingDate, type: MidweekMeetingPartType.OpenPrayer, userId: si.openprayer });
  if (si.cbs_reader)
    assignments.push({ date: meetingDate, type: MidweekMeetingPartType.CBSReader, userId: si.cbs_reader });
  if (si.closeprayer)
    assignments.push({ date: meetingDate, type: MidweekMeetingPartType.ClosePrayer, userId: si.closeprayer });
  si.tgw.concat(si.fm, si.lac).forEach((sa) => {
    const part = partsById.get(sa.part);
    if (part)
      assignments.push({
        date: meetingDate,
        type: part.type,
        userId: sa.assignee,
        classroom: sa.classroom,
        assistantId: sa.assistant,
      });
    if (sa.assistant)
      assignments.push({
        date: meetingDate,
        type: MidweekMeetingPartType.HH,
        userId: sa.assistant,
        classroom: sa.classroom,
        assistantId: sa.assignee,
      });
  });

  return assignments;
}

function wmScheduleToAssignments(wm: WMSchedule, congId: number): AssignmentSummary[] {
  const assignments: AssignmentSummary[] = [];
  const week = wm.date;
  if (wm.wm_chairman) assignments.push({ date: week, type: WMCoreParts.Chairman, userId: wm.wm_chairman });
  if (wm.wm_reader) assignments.push({ date: week, type: WMCoreParts.Reader, userId: wm.wm_reader });
  if (wm.host) assignments.push({ date: week, type: WMCoreParts.Host, userId: wm.host });
  if (wm.wt_conductor) assignments.push({ date: week, type: WMCoreParts.WTConductor, userId: wm.wt_conductor });
  if (wm.closeprayer) assignments.push({ date: week, type: WMCoreParts.ClosingPrayer, userId: wm.closeprayer });
  if (wm.interpreter) assignments.push({ date: week, type: WMCoreParts.Interpreter, userId: wm.interpreter });

  if (wm.speaker && wm.speaker.userId && wm.speaker.congregation?.id === congId) {
    assignments.push({ date: week, type: "publicTalk", userId: wm.speaker.userId });
  }
  if (Array.isArray(wm.out)) {
    wm.out.forEach((pta) => {
      if (pta.speaker?.userId) {
        const meetingDate = pta.congregation ? wmDate(week, pta.congregation) : weekOfString(week, 7);
        assignments.push({
          date: week,
          assignmentDate: meetingDate,
          type: "publicTalkOut",
          userId: pta.speaker.userId,
        });
      }
    });
  }

  return assignments;
}

function fsMeetingsToAssignments(
  schedules: OtherSchedule[],
  range: DateRange,
  locationsById: Map<number, FSMeetingLocation>,
): AssignmentSummary[] {
  const assignments: AssignmentSummary[] = [];

  schedules.forEach((os) => {
    if (!os.assigneeUserId) return; // TBD / assignee-text-only (e.g. circuit overseer) entries have no user to track
    const label = os.fsMeetingLocationId ? locationsById.get(os.fsMeetingLocationId)?.name : undefined;

    if (!os.weekly) {
      assignments.push({ date: os.date, type: OtherScheduleKind.FieldMinistry, userId: os.assigneeUserId, label });
      return;
    }

    // weekly recurring: expand to every occurrence of this day of week within the range, so
    // "last assigned" reflects the most recent actual meeting rather than the anchor date
    daysInRange(range.from, range.to, getDayjs(os.date).isoWeekday()).forEach((date) => {
      assignments.push({ date, type: OtherScheduleKind.FieldMinistry, userId: os.assigneeUserId!, label });
    });
  });

  return assignments;
}

//based on range, calculate a range of dates to look at when viewing assignments: 6 months before the beginning
//and 1 month after the end
export function assignmentMapDateRange(range: DateRange): DateRange {
  return {
    from: dateToString(getDayjs(range.from).subtract(12, "month").toDate()),
    to: dateToString(getDayjs(range.to).add(1, "month").toDate()),
  };
}

function mmOldestDate(date: ISODateString): ISODateString {
  const oldestAllowed = getDayjs(new Date()).subtract(1, "year").add(1, "week");
  const djs = getDayjs(date);
  if (djs.isBefore(oldestAllowed)) return oldestAllowed.format(ISODateFormat);
  return djs.format(ISODateFormat);
}

// buildAssignmentMap merges midweek/weekend/AV-attendant query results into a single map of
// userId -> AssignmentSummary[]. Pulled out of useAssignmentMap as a plain function so that
// useAssignmentMapsForCongs (which fetches this same data for a variable-length list of
// congregations via useQueries, see below) can reuse it without duplicating the merge logic.
function buildAssignmentMap(
  congSettings: CongSettings | undefined,
  mmsData: MMs | undefined,
  wmData: WMSchedule[] | undefined,
  avaData: AVAttendantAssignment[] | undefined,
  langGroupId: number,
  fmAssignments?: AssignmentSummary[],
): Map<number, AssignmentSummary[]> {
  const assignmentMap = new Map<number, AssignmentSummary[]>();
  if (!congSettings) return assignmentMap;

  const mergeAssignments = (assignments: AssignmentSummary[]) => {
    assignments.forEach((a) => {
      const entry = assignmentMap.get(a.userId);
      if (!entry) assignmentMap.set(a.userId, [a]);
      else entry.push(a);
    });
  };

  if (mmsData && Array.isArray(mmsData.meetings) && Array.isArray(mmsData.schedules)) {
    const partsById = new Map<number, MidweekMeetingPart>();
    //get a mapping of partId to part, for easy lookup
    mmsData.meetings.forEach((mi) => mi.tgw.concat(mi.fm, mi.lac).forEach((m) => partsById.set(m.id, m)));
    mmsData.schedules.forEach((si) => mergeAssignments(mmScheduleToAssignments(si, partsById)));
  }

  if (wmData && Array.isArray(wmData)) {
    wmData.forEach((wm) => mergeAssignments(wmScheduleToAssignments(wm, langGroupId)));
  }

  if (avaData && Array.isArray(avaData)) {
    const avaAssignments = avaData.flatMap((ava): AssignmentSummary | [] => {
      if (!ava.assignee) return [];
      return {
        date: ava.date,
        type: ava.type,
        userId: ava.assignee,
        slot: ava.slot,
        label: avaLabel(congSettings, ava.type, ava.slot),
      };
    });
    mergeAssignments(avaAssignments);
  }

  if (fmAssignments) mergeAssignments(fmAssignments);

  return assignmentMap;
}

// retrieve all the assignments, so they can be seen when searching for a new assignee
export function useAssignmentMap(
  dateRange: DateRange,
  enabled: boolean,
  langGroupId: number,
  opts?: { includeFieldMinistry?: boolean },
): Map<number, AssignmentSummary[]> {
  const congSettings = useCongSettings();
  const myDateRange = assignmentMapDateRange(dateRange);
  const includeFieldMinistry = !!opts?.includeFieldMinistry;

  //note that these queries have long staletimes. it's important that these query keys be updated
  //whenever assignments are changed so that this continues to reflect current data
  const mmDateFrom = mmOldestDate(myDateRange.from);
  // Only query if the date range is valid (from is before to)
  const mmDateRangeValid = dateStringCompare(mmDateFrom, myDateRange.to) < 0;
  const mmsQuery = useQuery({
    queryKey: QueryKeyBuilder.MidweekInfo(langGroupId, mmDateFrom, myDateRange.to, true),
    queryFn: () => midweekApi.getMeetingsInfo(mmDateFrom, myDateRange.to, langGroupId, true),
    enabled: enabled && mmDateRangeValid,
    staleTime: HourMs,
  });
  const wmQuery = useWMSchedules(myDateRange.from, myDateRange.to, langGroupId, {
    enabled: enabled,
    staleTime: HourMs,
  });
  const avaQuery = useAVAssignments(myDateRange.from, myDateRange.to, langGroupId, {
    enabled: enabled,
    staleTime: HourMs,
  });
  // field ministry (FS meeting) history is opt-in: only the FS meetings view needs it, so we
  // avoid an extra query/refetch cycle on every other scheduling page
  const fmQuery = useOtherSchedule(OtherScheduleKind.FieldMinistry, myDateRange.from, myDateRange.to, {
    enabled: enabled && includeFieldMinistry,
    staleTime: HourMs,
  });
  const fmLocationsQuery = useFSMeetingLocations({ enabled: enabled && includeFieldMinistry });

  if (!enabled || !congSettings.data) return new Map<number, AssignmentSummary[]>();

  const fmAssignments = includeFieldMinistry
    ? fsMeetingsToAssignments(
        fmQuery.data ?? [],
        myDateRange,
        new Map(fmLocationsQuery.data?.map((l) => [l.id, l]) ?? []),
      )
    : undefined;

  return buildAssignmentMap(congSettings.data, mmsQuery.data, wmQuery.data, avaQuery.data, langGroupId, fmAssignments);
}

// useAssignmentMapsForCongs fetches assignment maps and cong settings for an arbitrary, runtime-variable
// set of congregation ids (e.g. all language groups that meet on the same day as the host congregation)
// in a SINGLE hook call via useQueries. Callers must not do this by looping over congIds and calling
// useAssignmentMap/useCongSettings per iteration: the number of hook calls would then vary between
// renders whenever that congId list changes size (e.g. an admin adds/removes a language group in
// congProfile.tsx), which violates React's rules of hooks. useQueries is the supported pattern for a
// dynamically-sized set of queries behind one hook call.
//
// The query keys/functions below are intentionally duplicated from useWMSchedules, useAVAssignments,
// and useCongSettings (rather than calling those hooks, which isn't possible in a loop) so that this
// shares the same cache entries. Keep them in sync if those hooks' query keys or fetchers change.
export function useAssignmentMapsForCongs(
  congIds: number[],
  dateRange: DateRange,
  enabled: boolean,
): {
  assignmentMaps: Map<number, Map<number, AssignmentSummary[]>>;
  congSettings: Map<number, CongSettings | undefined>;
} {
  const myDateRange = assignmentMapDateRange(dateRange);
  const mmDateFrom = mmOldestDate(myDateRange.from);
  const mmDateRangeValid = dateStringCompare(mmDateFrom, myDateRange.to) < 0;

  const queries = useQueries({
    queries: congIds.flatMap((cid) => [
      {
        queryKey: QueryKeyBuilder.MidweekInfo(cid, mmDateFrom, myDateRange.to, true),
        queryFn: () => midweekApi.getMeetingsInfo(mmDateFrom, myDateRange.to, cid, true),
        enabled: enabled && mmDateRangeValid,
        staleTime: HourMs,
      },
      {
        queryKey: [QueryKeys.WMCSchedules, cid, myDateRange.from, myDateRange.to],
        queryFn: () => weekendApi.getSchedules(myDateRange.from, myDateRange.to, cid),
        enabled,
        staleTime: HourMs,
        meta: { clearStoredLangGroupOn404: true },
      },
      {
        queryKey: [QueryKeys.AVAttendantAssignment, cid, myDateRange.from, myDateRange.to],
        queryFn: () => avAttendantApi.getAssignmentsRange(myDateRange.from, myDateRange.to, cid),
        enabled,
        staleTime: HourMs,
        meta: { clearStoredLangGroupOn404: true },
      },
      {
        queryKey: [QueryKeys.CongregationSettings, cid],
        queryFn: () => congregationApi.getSettings(cid),
        staleTime: 15 * 60 * 1000,
        enabled: !!cid,
      },
    ]),
  });

  const assignmentMaps = new Map<number, Map<number, AssignmentSummary[]>>();
  const congSettingsMap = new Map<number, CongSettings | undefined>();

  congIds.forEach((cid, i) => {
    // safe by construction: `queries` has exactly congIds.length * 4 entries, one group of 4 per congId
    const [mmsQuery, wmQuery, avaQuery, congSettingsQuery] = queries.slice(i * 4, i * 4 + 4) as [
      (typeof queries)[number],
      (typeof queries)[number],
      (typeof queries)[number],
      (typeof queries)[number],
    ];
    const congSettingsData = congSettingsQuery.data as CongSettings | undefined;
    congSettingsMap.set(cid, congSettingsData);
    assignmentMaps.set(
      cid,
      enabled
        ? buildAssignmentMap(
            congSettingsData,
            mmsQuery.data as MMs | undefined,
            wmQuery.data as WMSchedule[] | undefined,
            avaQuery.data as AVAttendantAssignment[] | undefined,
            cid,
          )
        : new Map<number, AssignmentSummary[]>(),
    );
  });

  return { assignmentMaps, congSettings: congSettingsMap };
}

// summaryDate returns the date that an assignment occurred, depending on its type
function summaryDate(as: AssignmentSummary | null): ISODateString | undefined {
  if (!as) return undefined;

  const isAVA = Object.values(AVAttendantAssignmentType).includes(as.type as AVAttendantAssignmentType);
  const isMM = Object.values(MidweekMeetingPartType).includes(as.type as MidweekMeetingPartType);
  const isWM =
    Object.values(WMCoreParts).includes(as.type as WMCoreParts) ||
    as.type === "publicTalk" ||
    as.type === "publicTalkOut";

  if (isAVA) {
    const dow = getDayjs(as.date).isoWeekday();
    return dow >= 6 ? wmDate(as.date, HourglassGlobals.cong!) : mmDate(as.date, HourglassGlobals.cong!);
  } else if (isMM) {
    return mmDate(as.date, HourglassGlobals.cong!);
  } else if (isWM) {
    return wmDate(as.date, HourglassGlobals.cong!);
  }

  return as.date;
}

const attendantTypes: Set<AssignmentSummaryType> = new Set([
  AVAttendantAssignmentType.Attendant,
  AVAttendantAssignmentType.SecurityAttendant,
  AVAttendantAssignmentType.ZoomAttendant,
]);

const closingPrayerTypes: Set<AssignmentSummaryType> = new Set([
  MidweekMeetingPartType.ClosePrayer,
  WMCoreParts.ClosingPrayer,
]);

const openingPrayerTypes: Set<AssignmentSummaryType> = new Set([
  MidweekMeetingPartType.OpenPrayer,
  WMCoreParts.OpeningPrayer,
]);

// Helper function to check if two assignment types should be considered equivalent
// (e.g., both are closing prayers, regardless of midweek vs weekend)
function areTypesEquivalent(type1: AssignmentSummaryType, type2: AssignmentSummaryType): boolean {
  if (type1 === type2) return true;
  if (closingPrayerTypes.has(type1) && closingPrayerTypes.has(type2)) return true;
  if (openingPrayerTypes.has(type1) && openingPrayerTypes.has(type2)) return true;
  return attendantTypes.has(type1) && attendantTypes.has(type2);
}

function RecentAssignments(props: {
  date: ISODateString;
  possibleAssignees: User[];
  allGenderPossibleAssignees?: User[];
  partType:
    | MidweekMeetingPartType
    | AVAttendantAssignmentType
    | WMCoreParts
    | OtherScheduleKind.FieldMinistry
    | "publicTalkOut";
  assignmentMap: Map<number, AssignmentSummary[]>;
  selected: number;
  setSelected: (value: number) => any;
  userMap: Map<number, User>;
  categoryOnly: boolean;
  genderFilter?: UserGender;
  langGroupId?: number;
}) {
  const { t } = useTranslation();
  const absencesQuery = useAbsence();
  const congSettingsQuery = useCongSettings(props.langGroupId);
  const [wraper] = useAutoAnimate<HTMLDivElement>();
  const [sortField, setSortField] = useState<SortField>("type");
  const [sortAscending, setSortAscending] = useState(true);
  const [expandedUser, setExpandedUser] = useState(0);
  const [includeAssistant, setIncludeAssistant] = useState(true);

  const proxyDateStringCompare = (a: string | undefined, b: string | undefined) => {
    const _a = a === undefined ? "1970-01-01" : a;
    const _b = b === undefined ? "1970-01-01" : b;
    return dateStringCompare(_a, _b);
  };

  const absenceIncludes = (absence: Absence, date: ISODateString) => {
    const aDate = stringToDate(absence.start);
    const bDate = stringToDate(absence.end);
    return dateIsBetween(stringToDate(date), aDate, bDate);
  };
  const category = partCategory(props.partType);

  const recents = useMemo(() => {
    const possibleAssignees = props.allGenderPossibleAssignees
      ? props.allGenderPossibleAssignees
      : props.possibleAssignees;

    return possibleAssignees
      .filter((u) => (props.genderFilter ? u.sex === props.genderFilter : true))
      .map((u) => {
        const userParts = props.assignmentMap.get(u.id);
        const reverseDateSorted = (assignments: AssignmentSummary[]): AssignmentSummary[] => {
          return assignments
            .sort((a, b) => dateStringCompare(summaryDate(a) ?? a.date, summaryDate(b) ?? b.date))
            .reverse();
        };

        // For prayers, we want to show the most recent prayer regardless of midweek/weekend
        const isPrayer = closingPrayerTypes.has(props.partType) || openingPrayerTypes.has(props.partType);
        const filteredUserParts = userParts?.filter((a) => {
          if (!props.categoryOnly) return true;
          // For prayers, include all prayer types of the same kind (opening/closing)
          if (isPrayer) {
            return areTypesEquivalent(a.type, props.partType);
          }
          // For other types, filter by category as before
          return partCategory(a.type) === category;
        });
        const all = filteredUserParts?.length ? (reverseDateSorted(filteredUserParts)[0] ?? null) : null;
        const userPartsThisType = userParts?.length
          ? userParts.filter((a) => {
              if (props.partType === AVAttendantAssignmentType.Attendant) return attendantTypes.has(a.type);
              // Use equivalence check for prayers to include both midweek and weekend
              return areTypesEquivalent(a.type, props.partType);
            })
          : [];
        const type = userPartsThisType.length ? (reverseDateSorted(userPartsThisType)[0] ?? null) : null;

        // Calculate all prayers date if this is a prayer assignment
        const allPrayersTypes = [...closingPrayerTypes, ...openingPrayerTypes];
        const allPrayersParts = userParts?.filter((a) => allPrayersTypes.includes(a.type)) ?? [];
        const allPrayers = allPrayersParts.length ? (reverseDateSorted(allPrayersParts)[0] ?? null) : null;

        // Calculate all student assignments date if this is a student assignment
        const allStudentParts =
          userParts?.filter((a) => {
            const isStudent = StudentPartTypes.has(a.type as MidweekMeetingPartType);
            if (includeAssistant) return isStudent;
            return isStudent && a.type !== MidweekMeetingPartType.HH;
          }) ?? [];
        const allStudent = allStudentParts.length ? (reverseDateSorted(allStudentParts)[0] ?? null) : null;

        const isAbsent = !!absencesQuery.data?.some((a) => a.userId === u.id && absenceIncludes(a, props.date));

        const conflict = congSettingsQuery.data
          ? getOverlappingAssignmentLabel({
              userId: u.id,
              assignDate: props.date,
              partType: props.partType,
              assignmentMap: props.assignmentMap,
              congSettings: congSettingsQuery.data,
            })
          : null;

        return {
          id: u.id,
          name: u.displayName,
          all,
          type,
          allPrayers,
          allStudent,
          isAbsent,
          conflict,
        };
      })
      .sort((a, b) => {
        switch (sortField) {
          case "all":
            if (sortAscending) return proxyDateStringCompare(summaryDate(a.all), summaryDate(b.all));
            else return proxyDateStringCompare(summaryDate(b.all), summaryDate(a.all));
          case "type":
            if (sortAscending) return proxyDateStringCompare(summaryDate(a.type), summaryDate(b.type));
            else return proxyDateStringCompare(summaryDate(b.type), summaryDate(a.type));
          case "student":
            if (sortAscending) return proxyDateStringCompare(summaryDate(a.allStudent), summaryDate(b.allStudent));
            else return proxyDateStringCompare(summaryDate(b.allStudent), summaryDate(a.allStudent));
          case "prayer":
            if (sortAscending) return proxyDateStringCompare(summaryDate(a.allPrayers), summaryDate(b.allPrayers));
            else return proxyDateStringCompare(summaryDate(b.allPrayers), summaryDate(a.allPrayers));
          default:
            if (sortAscending) return a.name.localeCompare(b.name);
            else return b.name.localeCompare(a.name);
        }
      });
  }, [
    absencesQuery.data,
    congSettingsQuery.data,
    props.assignmentMap,
    props.partType,
    props.possibleAssignees,
    props.allGenderPossibleAssignees,
    sortAscending,
    sortField,
    props.date,
    props.genderFilter,
    props.categoryOnly,
    category,
    includeAssistant,
  ]);

  const partType = (type: AssignmentSummaryType): string => {
    const category = partCategory(type);
    if (category === NotificationType.Midweek) {
      const mmType = type as MidweekMeetingPartType;
      const title = t(MidweekMeetingPartAsset[mmType]);
      if (mmType === MidweekMeetingPartType.Chairman) return `${title} - ${t("conganalysis.attendance.midweek")}`;
      return title;
    }
    if (category === NotificationType.AVAttendant) {
      return t(AVAttendantAsset[type as AVAttendantAssignmentType]);
    }
    switch (type) {
      case "publicTalk":
      case "publicTalkOut":
        return t("schedules.weekend.speaker.0");
      default:
        break;
    }
    if (category === NotificationType.WeekendCRH) {
      const wmType = type as WMCoreParts;
      const title = t(WMCorePartTitleAsset[wmType]);
      // differentiate between midweek and weekend chairman
      if (wmType === WMCoreParts.Chairman) return `${title} - ${t("conganalysis.attendance.weekend")}`;
      return title;
    }
    if (category === NotificationType.FieldMinistry) {
      return t("schedules.field-service.title");
    }

    return type;
  };

  const setSortingColumn = (col: SortField) => {
    if (sortField === col) {
      setSortAscending(!sortAscending);
    } else {
      setSortField(col);
      setSortAscending(true);
    }
  };

  const SortingIcon = (props: { field: SortField }) => {
    if (sortField !== props.field) {
      return <ArrowDownUp className="me-1" />;
    }

    if (sortAscending) {
      return <ArrowDown className="me-1" />;
    }
    return <ArrowUp className="me-1" />;
  };

  const toggleExpandedUser = (id: number) => {
    if (expandedUser === id) setExpandedUser(0);
    else setExpandedUser(id);
  };

  const AssignmentDetails = (adProps: { asmt: AssignmentSummary }) => {
    // this adds some additional details about the assignment. for now, it's just the assistant
    if (adProps.asmt.assistantId) {
      const assistant = props.userMap.get(adProps.asmt.assistantId);
      const isAssistant = adProps.asmt.type === "hh";
      // if this is the assistant, we want to display the type of part it was. so we need to find the student assignment
      const student =
        assistant && isAssistant
          ? props.assignmentMap
              .get(assistant.id)
              ?.find(
                (as) =>
                  as.date === adProps.asmt.date &&
                  as.classroom === adProps.asmt.classroom &&
                  as.userId === assistant.id &&
                  as.assistantId === adProps.asmt.userId &&
                  as.type !== "hh",
              )
          : undefined;
      if (assistant) {
        return (
          <span style={{ backgroundColor: "initial" }}>
            {student && ` – ${partType(student.type)}`} (
            <Badge bg="light" text="dark" pill className="me-1">
              {isAssistant ? t("schedules.midweek.student") : t("schedules.midweek.assistant")}
            </Badge>
            {assistant.displayName})
          </span>
        );
      }
    }

    return null;
  };

  // Determine if we should show the additional column and its label
  const isPrayerPart = closingPrayerTypes.has(props.partType) || openingPrayerTypes.has(props.partType);
  const isStudentPart = StudentPartTypes.has(props.partType as MidweekMeetingPartType);
  const showAdditionalColumn = isPrayerPart || isStudentPart;
  const additionalColumnSort: SortField = isPrayerPart ? "prayer" : "student";
  const additionalColumnLabel = isPrayerPart
    ? t("schedules.prayer")
    : isStudentPart
      ? t("schedules.midweek.student")
      : "";

  const AssignmentHistory = (ahProps: { recentId: number }) => {
    const isPrayer = closingPrayerTypes.has(props.partType) || openingPrayerTypes.has(props.partType);
    return (
      <Table size="sm" className="ms-4 mt-1 recent-assignments">
        <tbody>
          {props.assignmentMap
            .get(ahProps.recentId)
            ?.filter((a) => {
              if (!props.categoryOnly) return true;
              // For prayers, include all prayer types of the same kind (opening/closing)
              if (isPrayer) {
                return areTypesEquivalent(a.type, props.partType);
              }
              // For other types, filter by category as before
              return partCategory(a.type) === category;
            })
            .sort((a, b) => dateStringCompare(summaryDate(a) ?? a.date, summaryDate(b) ?? b.date))
            .reverse()
            .slice(0, 20)
            .map((a, i) => {
              const isPrayerAssignment = closingPrayerTypes.has(a.type) || openingPrayerTypes.has(a.type);
              const isMidweekPrayer =
                a.type === MidweekMeetingPartType.OpenPrayer || a.type === MidweekMeetingPartType.ClosePrayer;
              const isWeekendPrayer = a.type === WMCoreParts.ClosingPrayer;

              return (
                <tr key={i}>
                  <td className={summaryDate(a) === props.date ? "bold" : undefined}>
                    {stringToLocaleDate(summaryDate(a))}
                  </td>
                  <td>
                    {partType(a.type)} <AssignmentDetails asmt={a} />
                    {isPrayerAssignment && isMidweekPrayer && (
                      <Badge className="ms-1" bg="secondary" text="light" pill>
                        {t("conganalysis.attendance.midweek")}
                      </Badge>
                    )}
                    {isPrayerAssignment && isWeekendPrayer && (
                      <Badge className="ms-1" bg="secondary" text="light" pill>
                        {t("conganalysis.attendance.weekend")}
                      </Badge>
                    )}
                    {a.label && partType(a.type) !== a.label && <span className="ms-1"> ({a.label})</span>}
                    {!!a.classroom && (
                      <Badge className="ms-1" bg="secondary" text="light" pill>
                        {a.classroom + 1}
                      </Badge>
                    )}
                  </td>
                </tr>
              );
            })}
        </tbody>
      </Table>
    );
  };

  return (
    <ListGroup ref={wraper}>
      <ListGroupItem>
        <Row className="bold cursor-pointer ">
          <Col className="overflow-hidden text-nowrap text-truncate" onClick={() => setSortingColumn("name")}>
            <SortingIcon field="name" />
            {t("general.name")}
          </Col>
          <Col
            xs="4"
            md="4"
            lg="3"
            className="overflow-hidden text-nowrap text-truncate"
            onClick={() => setSortingColumn("all")}
          >
            <SortingIcon field="all" />
            {t("schedules.fill.all")}
          </Col>
          {showAdditionalColumn && (
            <Col
              xs="4"
              md="4"
              lg="3"
              className="overflow-hidden text-nowrap text-truncate"
              onClick={() => setSortingColumn(additionalColumnSort)}
            >
              <SortingIcon field={additionalColumnSort} />
              {additionalColumnLabel}
              {additionalColumnSort === "student" && (
                <div onClick={(e) => e.stopPropagation()}>
                  <Form.Check
                    type="checkbox"
                    id="include-assistant-checkbox"
                    checked={includeAssistant}
                    onChange={(e) => {
                      e.stopPropagation();
                      setIncludeAssistant(!includeAssistant);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    label={t("schedules.midweek.assistant")}
                    className="d-inline-block ms-2"
                  />
                </div>
              )}
            </Col>
          )}
          {!!props.partType && (
            <Col
              xs="4"
              md="4"
              lg="3"
              className="overflow-hidden text-nowrap text-truncate"
              onClick={() => setSortingColumn("type")}
            >
              <SortingIcon field="type" />
              {partType(props.partType)}
            </Col>
          )}
        </Row>
      </ListGroupItem>
      {recents.map((r) => {
        const isPrayer = closingPrayerTypes.has(props.partType) || openingPrayerTypes.has(props.partType);
        const typeIsMidweekPrayer =
          r.type &&
          (r.type.type === MidweekMeetingPartType.OpenPrayer || r.type.type === MidweekMeetingPartType.ClosePrayer);
        const typeIsWeekendPrayer = r.type && r.type.type === WMCoreParts.ClosingPrayer;

        return (
          <ListGroupItem
            key={r.id}
            action
            onClick={() => props.setSelected(r.id)}
            disabled={props.partType !== WMCoreParts.Host && r.isAbsent}
          >
            <Row className={props.selected === r.id ? "text-bold" : ""}>
              <Col>
                <span
                  className="me-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleExpandedUser(r.id);
                  }}
                >
                  {r.id === expandedUser ? <DashSquare /> : <PlusSquare />}
                </span>
                {r.name}
                {!!r.conflict && <OverlappingAssignment label={r.conflict} className="ms-1" />}
                {r.isAbsent && " *"}
              </Col>
              <Col xs="4" md="4" lg="3">
                {summaryDate(r.all) ? stringToLocaleDate(summaryDate(r.all)) : "-"}
              </Col>
              {showAdditionalColumn && (
                <Col xs="4" md="4" lg="3">
                  {isPrayerPart && summaryDate(r.allPrayers)
                    ? stringToLocaleDate(summaryDate(r.allPrayers))
                    : isStudentPart && summaryDate(r.allStudent)
                      ? stringToLocaleDate(summaryDate(r.allStudent))
                      : "-"}
                </Col>
              )}
              {!!props.partType && (
                <Col xs="4" md="4" lg="3">
                  {summaryDate(r.type) ? stringToLocaleDate(summaryDate(r.type)) : "-"}
                  {isPrayer && typeIsMidweekPrayer && (
                    <Badge className="ms-1" bg="secondary" text="light" pill>
                      {t("conganalysis.attendance.midweek")}
                    </Badge>
                  )}
                  {isPrayer && typeIsWeekendPrayer && (
                    <Badge className="ms-1" bg="secondary" text="light" pill>
                      {t("conganalysis.attendance.weekend")}
                    </Badge>
                  )}
                </Col>
              )}
            </Row>
            {r.id === expandedUser && <AssignmentHistory recentId={r.id} />}
          </ListGroupItem>
        );
      })}
    </ListGroup>
  );
}

export function SearchAssigneeModal(props: {
  date: string;
  assignee?: User;
  possibleAssignees: User[];
  allGenderPossibleAssignees?: User[];
  partType:
    | MidweekMeetingPartType
    | AVAttendantAssignmentType
    | WMCoreParts
    | OtherScheduleKind.FieldMinistry
    | "publicTalkOut";
  show: boolean;
  setShow: (show: boolean) => void;
  assign: (userId: number) => Promise<void>;
  assignmentMap: Map<number, AssignmentSummary[]>;
  heading: React.JSX.Element;
  userMap: Map<number, User>;
  className?: string;
  langGroupId?: number;
}) {
  const { t } = useTranslation();
  const [categoryOnly, setCategoryOnly] = useState(props.partType === "publicTalkOut");
  const [assistantOnly, setAssistantOnly] = useState(false);
  const [genderFilter, setGenderFilter] = useState<UserGender>();
  const [pending, setPending] = useState(false);
  const [selected, setSelected] = useState(props.assignee?.id ?? 0);

  useEffect(() => {
    //if the assignee in props changes, that means a new person was selected. we need to update our state to reflect it.
    setSelected(props.assignee?.id ?? 0);
  }, [props.assignee]);

  const possibleAssignees = props.assignee
    ? props.possibleAssignees.concat(props.assignee).sort(userCompare)
    : props.possibleAssignees;

  const allGenderPossibleAssignees =
    props.assignee && props.allGenderPossibleAssignees
      ? props.allGenderPossibleAssignees.concat(props.assignee).sort(userCompare)
      : props.allGenderPossibleAssignees;

  const hasAssistant = (): boolean => {
    return (
      props.partType === MidweekMeetingPartType.InitialCall ||
      props.partType === MidweekMeetingPartType.RV ||
      props.partType === MidweekMeetingPartType.StudentBibleStudy
    );
  };

  const handleClose = () => {
    props.setShow(false);
    setPending(false);
    setCategoryOnly(false);
    setGenderFilter(undefined);
  };

  const assignPart = async () => {
    setPending(true);
    await props.assign(selected);
    handleClose();
  };

  const getCategoryName = (): string | undefined => {
    switch (partCategory(props.partType)) {
      case NotificationType.Midweek:
        return t("nav.schedules.meetings.midweek");
      case NotificationType.WeekendCRH:
        return t("nav.schedules.meetings.weekend");
      case NotificationType.AVAttendant:
        return t("schedules.ava.title");
      case NotificationType.FieldMinistry:
        return t("schedules.field-service.title");
    }
  };

  const categoryName = getCategoryName();
  const partType = assistantOnly ? MidweekMeetingPartType.HH : props.partType;

  return (
    <Modal show={props.show} onHide={handleClose} size="lg" scrollable className={props.className}>
      <Modal.Header closeButton>
        <Modal.Title>{stringToLocaleDate(props.date)}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {props.heading}

        <div className="d-flex justify-content-between">
          <Form.Group className="mb-1">
            {props.partType !== "publicTalkOut" && (
              <>
                <Form.Label>{t("userinfo.gender.0")}</Form.Label>
                <Form.Select
                  onChange={(e) => {
                    switch (e.currentTarget.value) {
                      case UserGender.Female:
                        setGenderFilter(UserGender.Female);
                        break;
                      case UserGender.Male:
                        setGenderFilter(UserGender.Male);
                        break;
                      default:
                        setGenderFilter(undefined);
                    }
                  }}
                  value={genderFilter ?? ""}
                >
                  <option value="" />
                  <option value={UserGender.Male}>{t("userinfo.gender.male")}</option>
                  <option value={UserGender.Female}>{t("userinfo.gender.female")}</option>
                </Form.Select>
              </>
            )}
          </Form.Group>

          <div>
            <b className="me-1">{t("contactlist.filter-by")}</b>
            {!!categoryName && (
              <Form.Check
                id="typeOnly"
                checked={categoryOnly}
                onChange={() => setCategoryOnly(!categoryOnly)}
                label={categoryName}
                className="me-4"
              />
            )}
            {hasAssistant() && (
              <Form.Check
                id="assistantOnly"
                checked={assistantOnly}
                onChange={() => setAssistantOnly(!assistantOnly)}
                label={t("schedules.midweek.assistant")}
                className="me-4"
              />
            )}
          </div>
        </div>
        <RecentAssignments
          date={props.date}
          assignmentMap={props.assignmentMap}
          possibleAssignees={possibleAssignees}
          allGenderPossibleAssignees={allGenderPossibleAssignees}
          partType={partType}
          selected={selected}
          setSelected={setSelected}
          userMap={props.userMap}
          categoryOnly={categoryOnly}
          genderFilter={genderFilter}
          langGroupId={props.langGroupId}
        />
      </Modal.Body>
      <Modal.Footer>
        {pending && <Spinner animation="border" size="sm" className="me-2" />}
        <Button
          variant="primary"
          onClick={assignPart}
          disabled={pending || !selected || selected === props.assignee?.id}
        >
          {t("schedules.assignment.assign")}
        </Button>
        <Button variant="primary" onClick={handleClose}>
          {t("general.cancel")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

function partCategory(partType: AssignmentSummaryType): NotificationType | undefined {
  if (Object.values(MidweekMeetingPartType).includes(partType as MidweekMeetingPartType)) {
    return NotificationType.Midweek;
  }
  if (Object.values(AVAttendantAssignmentType).includes(partType as AVAttendantAssignmentType)) {
    return NotificationType.AVAttendant;
  }
  if (
    Object.values(WMCoreParts).includes(partType as WMCoreParts) ||
    partType === "publicTalk" ||
    partType === "publicTalkOut"
  ) {
    return NotificationType.WeekendCRH;
  }
  if (partType === OtherScheduleKind.FieldMinistry) {
    return NotificationType.FieldMinistry;
  }
}
